import type { SgIncorporationForm, SgPepRow, SgShareholderRow } from '../components/sg/types/sgIncorporation.types';
import {
  createEmptyCsDirector,
  createEmptyCsShareholder,
  createEmptyDirector,
  createEmptyPep,
  createEmptyShareholder,
  createEmptyUbo,
  createSgIncorporationForm,
  emptyControllerNoticeMeta,
} from '../components/sg/composables/createSgIncorporationForm';
import {
  formatSgAddressLine,
  normalizeSgAddress,
  normalizeSgAddressFromLegacy,
  createEmptySgAddress,
} from '../components/sg/utils/sgLocalAddress';
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';
import { stripSgFormFileBlobs, newFileCd } from './sgIncorporationFiles';

function formatSplitPhone(countryCode?: string, areaCode?: string, number?: string): string {
  return [countryCode, areaCode, number].map((part) => part?.trim()).filter(Boolean).join(' ');
}

function formatSgMobileNumber(company: SgIncorporationForm['company']): string {
  return formatSplitPhone(
    company.entityMobileCountryCode,
    company.entityMobileAreaCode,
    company.entityMobileNo,
  );
}

function formatAgentContactNo(company: SgIncorporationForm['company']): string {
  return formatSplitPhone(
    company.agentMobileCountryCode,
    company.agentMobileAreaCode,
    company.agentMobileNo,
  );
}

function formatDirectorMobileNo(director: SgIncorporationForm['directors'][0]): string {
  return formatSplitPhone(
    director.directorMobileCountryCode,
    director.directorMobileAreaCode,
    director.directorMobileNo,
  );
}

function formatDirectorTelNo(director: SgIncorporationForm['directors'][0]): string {
  return formatSplitPhone(
    director.directorTelCountryCode,
    director.directorTelAreaCode,
    director.directorTelNo,
  );
}

function splitExecutionDate(dateStr: string): { execution_day: string; execution_month: string; execution_year: string } {
  if (!dateStr) return { execution_day: '', execution_month: '', execution_year: '' };
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { execution_day: '', execution_month: '', execution_year: '' };
  return {
    execution_day: String(d.getDate()),
    execution_month: d.toLocaleString('en', { month: 'long' }),
    execution_year: String(d.getFullYear()),
  };
}

function formatShareholderMobileNo(s: SgIncorporationForm['shareholders'][0]): string {
  return formatSplitPhone(s.mobileCountryCode, s.mobileAreaCode, s.mobileNo);
}

function buildDirectorRow(d: SgIncorporationForm['directors'][0]) {
  const exec = splitExecutionDate(d.executionDate);
  const directorMobile = formatDirectorMobileNo(d);
  const directorTel = formatDirectorTelNo(d);
  return {
    name: d.directorName,
    director_name: d.directorName,
    director_address: formatSgAddressLine(d.directorAddress),
    director_alternate_address: formatSgAddressLine(d.directorAlternateAddress),
    director_id_type: d.directorIdType,
    director_identification_no: d.directorIdentificationNo,
    director_nationality: d.directorNationality,
    director_date_of_birth: d.directorDateOfBirth
      ? formatDateByJurisdiction(d.directorDateOfBirth)
      : '',
    director_email: d.directorEmail,
    director_tel_no: directorTel,
    director_mobile_no: directorMobile,
    director_contact_no: directorMobile || directorTel,
    director_area_code_not_applicable: d.directorAreaCodeNotApplicable,
    director_is_pep: d.directorIsPep,
    appointment_effective_date: d.appointmentEffectiveDate
      ? formatDateByJurisdiction(d.appointmentEffectiveDate)
      : '',
    ...exec,
  };
}

/** Client Acceptance template uses director.address dot notation */
function mapDirectorForTemplate(d: ReturnType<typeof buildDirectorRow>) {
  return {
    ...d,
    address: d.director_address,
    identification_no: d.director_identification_no,
    nationality: d.director_nationality,
    tel_no: d.director_tel_no,
    mobile_no: d.director_mobile_no,
    email: d.director_email,
    date_of_birth: d.director_date_of_birth,
    is_pep: d.director_is_pep,
  };
}

function firstCorporateShareholder(shareholders: SgShareholderRow[]) {
  return shareholders.find((s) => s.shareholderType === 'corporate');
}

function hasCorporateShareholder(shareholders: SgShareholderRow[]) {
  return shareholders.some((s) => s.shareholderType === 'corporate');
}

function normalizePersonKeyPart(value: string | undefined | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function pepDetailFilledScore(detail: SgPepRow): number {
  return [
    detail.category,
    detail.relationship,
    detail.name,
    detail.country,
    detail.function,
    detail.period,
  ].filter((v) => String(v || '').trim()).length;
}

function mergePepDetails(a: SgPepRow, b: SgPepRow): SgPepRow {
  const prefer = pepDetailFilledScore(b) > pepDetailFilledScore(a) ? b : a;
  const other = prefer === a ? b : a;
  return {
    category: prefer.category || other.category || '',
    relationship: prefer.relationship || other.relationship || '',
    name: prefer.name || other.name || '',
    country: prefer.country || other.country || '',
    function: prefer.function || other.function || '',
    period: prefer.period || other.period || '',
  };
}

/**
 * Director / Individual shareholder / UBO 의 PEP 를 수집.
 * 동일 인물(이름 또는 식별번호)이면 한 건만 유지하고 pepDetail 은 더 채워진 쪽으로 병합.
 */
function collectPepsFromForm(form: SgIncorporationForm): SgPepRow[] {
  type PepEntry = { personName: string; identificationNo: string; detail: SgPepRow };
  const entries: PepEntry[] = [];

  const pushPep = (
    detail: SgPepRow | undefined,
    personName: string,
    identificationNo = '',
  ) => {
    const person = personName.trim();
    const pepName = (detail?.name || person || '').trim();
    if (!pepName && !person) return;
    const nextDetail: SgPepRow = { ...createEmptyPep(), ...detail, name: pepName || person };
    entries.push({
      personName: person || pepName,
      identificationNo: identificationNo.trim(),
      detail: nextDetail,
    });
  };

  for (const d of form.directors) {
    if (d.directorIsPep) {
      pushPep(d.pepDetail, d.directorName, d.directorIdentificationNo);
    }
  }
  for (const s of form.shareholders) {
    if (s.shareholderType === 'individual' && s.isPep) {
      pushPep(s.pepDetail, s.name, s.identificationNo);
    }
    if (s.shareholderType === 'corporate') {
      for (const ubo of s.ubos ?? []) {
        if (ubo.isPep) pushPep(ubo.pepDetail, ubo.name, ubo.identificationNo);
      }
    }
  }

  const merged: PepEntry[] = [];
  for (const entry of entries) {
    const nameKey = normalizePersonKeyPart(entry.personName);
    const idKey = normalizePersonKeyPart(entry.identificationNo);
    const existing = merged.find((m) => {
      const mId = normalizePersonKeyPart(m.identificationNo);
      const mName = normalizePersonKeyPart(m.personName);
      if (idKey && mId && idKey === mId) return true;
      if (nameKey && mName && nameKey === mName) return true;
      return false;
    });
    if (!existing) {
      merged.push(entry);
      continue;
    }
    existing.detail = mergePepDetails(existing.detail, entry.detail);
    if (!existing.identificationNo && entry.identificationNo) {
      existing.identificationNo = entry.identificationNo;
    }
    if (!existing.personName && entry.personName) {
      existing.personName = entry.personName;
    }
  }

  return merged.map((m) => m.detail);
}

function collectUbosFromShareholders(shareholders: SgShareholderRow[]) {
  return shareholders
    .filter((s) => s.shareholderType === 'corporate')
    .flatMap((s) => (s.ubos ?? []).filter((u) => u.name?.trim()))
    .map((u) => ({
      name: u.name,
      identification_no: u.identificationNo,
      ownership_structure: u.ownershipStructure,
      residential_address: formatSgAddressLine(u.residentialAddress),
      nationality: u.nationality,
      date_of_birth: u.dateOfBirth ? formatDateByJurisdiction(u.dateOfBirth) : '',
      contact_no: u.contactNo,
      email: u.email,
      is_pep: u.isPep,
    }));
}

/** Flatten SG wizard form → Word template variables (snake_case) */
export function buildSgDocumentVariables(form: SgIncorporationForm): Record<string, unknown> {
  const { company, engagement, declaration, risk } = form;
  const companyName = company.entityName || engagement.proposedCompanyName || '';
  const formShareholders =
    form.includeShareholders === 'yes' ? form.shareholders : [];
  const corporateSh = firstCorporateShareholder(formShareholders);

  const directors =
    form.includeDirectors === 'yes'
      ? form.directors.map((d) => mapDirectorForTemplate(buildDirectorRow(d)))
      : [];
  const secretary =
    form.includeSecretary === 'yes'
      ? form.secretary
      : {
          secretaryName: '',
          secretaryAddress: createEmptySgAddress(),
          secretaryIdType: '',
          secretaryIdentificationNo: '',
          secretaryNationality: '',
          secretaryEmail: '',
          secretaryMobileCountryCode: '',
          secretaryMobileAreaCode: '',
          secretaryMobileNo: '',
          secretaryAreaCodeNotApplicable: false,
          appointmentEffectiveDate: '',
          executionDate: '',
        };

  const shareholders = formShareholders.map((s) => {
    const dateOfBirth = s.dateOfBirth ? formatDateByJurisdiction(s.dateOfBirth) : '';
    const mobileNo = formatShareholderMobileNo(s);
    const base = {
      name: s.name,
      nationality: s.nationality,
      id_type: s.idType,
      identification_no: s.identificationNo,
      no_of_shares: s.noOfShares,
      number_of_shares: s.noOfShares,
      share_percentage: s.sharePercentage,
      share_type: s.shareType,
      held_in_trust: s.heldInTrust,
      provide_name_of_trust: s.provideNameOfTrust,
      name_of_trust: s.nameOfTrust,
      sub_classes: (s.subClasses ?? []).map((x) => ({ subclass_name: x.subclassName, share_allotted: x.shareAllotted })),
      allotments: (s.allotments ?? []).map((a) => ({
        currency: a.currency,
        share_type: a.shareType,
        no_of_shares: a.noOfShares,
        paid_up_capital: a.paidUpCapital,
        held_in_trust: a.heldInTrust,
        provide_name_of_trust: a.provideNameOfTrust,
        name_of_trust: a.nameOfTrust,
        sub_classes: (a.subClasses ?? []).map((x) => ({
          subclass_name: x.subclassName,
          share_allotted: x.shareAllotted,
        })),
      })),
      shareholder_type: s.shareholderType,
      address: formatSgAddressLine(s.address),
      date_of_birth: dateOfBirth,
      shareholder_date_of_birth: dateOfBirth,
      email: s.email,
      mobile_no: mobileNo,
      shareholder_mobile_no: mobileNo,
      is_pep: s.isPep,
    };
    if (s.shareholderType === 'corporate') {
      return {
        ...base,
        cs_company_name: s.csCompanyName,
          cs_registration_no: s.csRegistrationNo,
          cs_former_name: s.csFormerName,
          cs_country_of_incorporation: s.csCountryOfIncorporation,
          cs_incorporation_date: s.csIncorporationDate
            ? formatDateByJurisdiction(s.csIncorporationDate)
            : '',
          cs_business_activity: s.csBusinessActivityName || s.csBusinessActivityCode,
          cs_paid_up_capital: s.csPaidUpCapital,
          cs_no_of_shares_issued: s.csNoOfSharesIssued,
          cs_registered_address: formatSgAddressLine(s.csRegisteredAddress),
          cs_last_financial_year: s.csLastFinancialYear,
          cs_website: s.csWebsite,
          cs_is_public_listed: s.csIsPublicListed,
          cs_contact_person: s.csContactPerson,
          cs_contact_no_office: formatSplitPhone(
            s.csContactOfficeCountryCode,
            s.csContactOfficeAreaCode,
            s.csContactNoOffice,
          ),
          cs_email: s.csEmail,
          cs_mobile_no: formatSplitPhone(
            s.csMobileCountryCode,
            s.csMobileAreaCode,
            s.csMobileNo,
          ),
          cs_directors: s.csDirectors.map((d) => ({
            no: d.no,
            name: d.name,
            nationality: d.nationality,
            identification_no: d.identificationNo,
            residential_address: formatSgAddressLine(d.residentialAddress),
            date_of_appointment: d.dateOfAppointment
              ? formatDateByJurisdiction(d.dateOfAppointment)
              : '',
          })),
          cs_shareholders: s.csShareholders.map((sh) => ({
            no: sh.no,
            name: sh.name,
            nationality: sh.nationality,
            identification_no: sh.identificationNo,
            residential_address: formatSgAddressLine(sh.residentialAddress),
            no_of_shares_and_percentage: sh.noOfSharesAndPercentage,
            date_of_appointment: sh.dateOfAppointment
              ? formatDateByJurisdiction(sh.dateOfAppointment)
              : '',
          })),
        };
    }
    return base;
  });

  const corporateControllers =
    form.includeControllers === 'yes'
      ? form.corporateControllers.map((c) => ({
    controller_corporate_name: c.controllerCorporateName,
    controller_uen: c.controllerUen,
    controller_registered_office: formatSgAddressLine(c.controllerRegisteredOffice),
    controller_email: c.controllerEmail,
    controller_contact_no: formatSplitPhone(
      c.controllerContactCountryCode,
      c.controllerContactAreaCode,
      c.controllerContactNo,
    ),
    controller_legal_form: c.controllerLegalForm,
    controller_jurisdiction_statute: c.controllerJurisdictionStatute,
    controller_register_name: c.controllerRegisterName,
    controller_registration_no: c.controllerRegistrationNo,
    controller_became_date: c.controllerBecameDate
      ? formatDateByJurisdiction(c.controllerBecameDate)
      : '',
    acknowledgement_signer_name: c.acknowledgementSignerName,
    acknowledgement_signer_role: c.acknowledgementSignerRole,
    notice_date: c.noticeDate ? formatDateByJurisdiction(c.noticeDate) : '',
    notice_recipient_name: c.noticeRecipientName,
    reply_to_address: formatSgAddressLine(c.replyToAddress),
    notice_signer_name: c.noticeSignerName,
  }))
      : [];

  const individualControllers =
    form.includeControllers === 'yes'
      ? form.individualControllers.map((c) => ({
    controller_full_name: c.controllerFullName,
    controller_aliases: c.controllerAliases,
    controller_residential_address: formatSgAddressLine(c.controllerResidentialAddress),
    controller_email: c.controllerEmail,
    controller_contact_no: formatSplitPhone(
      c.controllerContactCountryCode,
      c.controllerContactAreaCode,
      c.controllerContactNo,
    ),
    controller_nationality: c.controllerNationality,
    controller_id_type: c.controllerIdType,
    controller_identification_no: c.controllerIdentificationNo,
    controller_date_of_birth: c.controllerDateOfBirth
      ? formatDateByJurisdiction(c.controllerDateOfBirth)
      : '',
    controller_became_date: c.controllerBecameDate
      ? formatDateByJurisdiction(c.controllerBecameDate)
      : '',
    notice_date: c.noticeDate ? formatDateByJurisdiction(c.noticeDate) : '',
    notice_recipient_name: c.noticeRecipientName,
    reply_to_address: formatSgAddressLine(c.replyToAddress),
    notice_signer_name: c.noticeSignerName,
  }))
      : [];

  const managingDirectors = (company.managingDirectors ?? []).filter((r) => r.name?.trim());
  const ceos = (company.ceos ?? []).filter((r) => r.name?.trim());
  const partners = (company.partners ?? []).filter((r) => r.name?.trim());
  const others = (company.others ?? []).filter((r) => r.name?.trim());
  const ubos = collectUbosFromShareholders(formShareholders);
  const peps = collectPepsFromForm(form).map((p) => ({
    category: p.category,
    relationship: p.relationship,
    name: p.name,
    country: p.country,
    function: p.function,
    period: p.period,
  }));

  const srvIfFlags = Object.fromEntries(
    Object.entries(risk.srvA).map(([k, v]) => [`if_${k}`, v]),
  );
  const b1IfFlags = Object.fromEntries(
    Object.entries(risk.b1Q).map(([k, v]) => [`if_${k}`, v]),
  );
  const b2IfFlags = Object.fromEntries(
    Object.entries(risk.b2Q).map(([k, v]) => [`if_${k}`, v]),
  );

  return {
    // Constitution Cover Page
    company_registration_no: company.entityUen,
    company_name: companyName,
    date_of_incorporation: company.entityIncorporationDate
      ? formatDateByJurisdiction(company.entityIncorporationDate)
      : '',
    authorized_person_name: declaration.authorizedPersonName,
    authorized_person_title: declaration.authorizedPersonTitle,
    certification_date: declaration.certificationDate
      ? formatDateByJurisdiction(declaration.certificationDate)
      : '',

    // Client Acceptance — entity
    entity_name: company.entityName,
    entity_former_names: company.entityFormerNames,
    entity_uen: company.entityUen,
    entity_registered_address: formatSgAddressLine(company.entityRegisteredAddress),
    entity_principal_business_address: formatSgAddressLine(company.entityPrincipalBusinessAddress),
    entity_mailing_address: formatSgAddressLine(company.entityMailingAddress),
    entity_incorporation_date: company.entityIncorporationDate
      ? formatDateByJurisdiction(company.entityIncorporationDate)
      : '',
    entity_contact_person: company.entityContactPerson,
    entity_mobile_number: formatSgMobileNumber(company),
    entity_email: company.entityEmail,
    entity_business_activity: company.entityPrimaryActivityName
      || company.entityPrimaryActivityCode,
    entity_share_capital: company.entityShareCapital,
    entity_share_value: company.entityShareValue,
    entity_proposed_fye: company.entityProposedFye
      ? formatDateByJurisdiction(company.entityProposedFye)
      : '',
    operating_hour_type: company.operatingHourType,
    operating_hours: company.operatingHourType === '3TO5' ? company.operatingHours : '',
    share_capitals: (form.shareStructure?.currencies ?? []).map((c) => ({
      currency: c.currency,
      payable_method: c.payableMethod,
      allotment_method: c.allotmentMethod,
      allotment_method_desc: c.allotmentMethodDesc,
      share_classes: c.shareClasses.map((k) => ({
        share_type: k.shareType,
        issued_cap_amount: k.issuedCapAmount,
        paidup_cap_amount: k.paidupCapAmount,
        share_allotted: k.shareAllotted,
        sub_classes: k.subClasses.map((s) => ({ subclass_name: s.subclassName, share_allotted: s.shareAllotted })),
      })),
    })),
    share_groups: (form.shareStructure?.groups ?? []).map((g) => ({
      group_name: g.groupName,
      shareholder_indices: g.shareholderIndices,
      lines: g.lines.map((l) => ({
        currency: l.currency,
        share_type: l.shareType,
        share_allotted: l.shareAllotted,
        sub_classes: l.subClasses.map((s) => ({ subclass_name: s.subclassName, share_allotted: s.shareAllotted })),
      })),
    })),
    has_corporate_shareholder: hasCorporateShareholder(formShareholders),
    ihc_name: corporateSh?.ihcName ?? '',
    ihc_industry: corporateSh?.ihcIndustry ?? '',
    ihc_country: corporateSh?.ihcCountry ?? '',
    ihc_is_public: corporateSh?.ihcIsPublic ?? false,
    ihc_registration_no: corporateSh?.ihcRegistrationNo ?? '',
    ihc_stock_exchange: corporateSh?.ihcStockExchange ?? '',
    ihc_ownership_structure: corporateSh?.ihcOwnershipStructure ?? '',
    upc_name: corporateSh?.upcName ?? '',
    upc_industry: corporateSh?.upcIndustry ?? '',
    upc_country: corporateSh?.upcCountry ?? '',
    upc_is_public: corporateSh?.upcIsPublic ?? false,
    upc_registration_no: corporateSh?.upcRegistrationNo ?? '',
    upc_stock_exchange: corporateSh?.upcStockExchange ?? '',
    upc_ownership_structure: corporateSh?.upcOwnershipStructure ?? '',
    has_pep: peps.length > 0,
    peps,
    ubos,
    source_of_wealth_info: company.sourceOfWealthInfo,
    business_website: company.businessWebsite,
    supporting_documents_notes: company.supportingDocumentsNotes,
    agent_name: company.agentName,
    agent_identification_no: company.agentIdentificationNo,
    agent_company_name: company.agentCompanyName,
    agent_nationality: company.agentNationality,
    agent_residential_address: formatSgAddressLine(company.agentResidentialAddress),
    agent_contact_no: formatAgentContactNo(company),
    agent_date_of_birth: company.agentDateOfBirth
      ? formatDateByJurisdiction(company.agentDateOfBirth)
      : '',
    agent_job_title: company.agentJobTitle,
    agent_email: company.agentEmail,
    managing_directors: managingDirectors.map((r) => ({ name: r.name })),
    ceos: ceos.map((r) => ({ name: r.name, ceo_name: r.name })),
    partners: partners.map((r) => ({ name: r.name, p_name: r.name })),
    others: others.map((r) => ({ name: r.name, o_name: r.name, o_role: r.role ?? '' })),
    has_managing_directors: managingDirectors.length > 0,
    has_ceos: ceos.length > 0,
    has_partners: partners.length > 0,
    has_others: others.length > 0,
    has_ubos: ubos.length > 0,
    if_directors: directors.length > 0,

    directors,
    shareholders,

    // Form 45B
    secretary_name: secretary.secretaryName,
    secretary_address: formatSgAddressLine(secretary.secretaryAddress),
    secretary_id_type: secretary.secretaryIdType,
    secretary_identification_no: secretary.secretaryIdentificationNo,
    secretary_nationality: secretary.secretaryNationality,
    secretary_email: secretary.secretaryEmail,
    secretary_mobile_no: formatSplitPhone(
      secretary.secretaryMobileCountryCode,
      secretary.secretaryAreaCodeNotApplicable ? '' : secretary.secretaryMobileAreaCode,
      secretary.secretaryMobileNo,
    ),
    secretary_area_code_not_applicable: secretary.secretaryAreaCodeNotApplicable,
    appointment_effective_date: secretary.appointmentEffectiveDate
      ? formatDateByJurisdiction(secretary.appointmentEffectiveDate)
      : '',
    execution_date: secretary.executionDate
      ? formatDateByJurisdiction(secretary.executionDate)
      : '',

    // Controller notices
    notice_date:
      corporateControllers[0]?.notice_date
      || individualControllers[0]?.notice_date
      || declaration.noticeDate,
    notice_recipient_name:
      corporateControllers[0]?.notice_recipient_name
      || individualControllers[0]?.notice_recipient_name
      || declaration.noticeRecipientName,
    reply_to_address:
      corporateControllers[0]?.reply_to_address
      || individualControllers[0]?.reply_to_address
      || declaration.replyToAddress,
    notice_signer_name:
      corporateControllers[0]?.notice_signer_name
      || individualControllers[0]?.notice_signer_name
      || declaration.noticeSignerName,
    corporate_controllers: corporateControllers,
    individual_controllers: individualControllers,

    // Engagement Letter
    proposed_company_name:
      form.includeEngagement === 'yes'
        ? engagement.proposedCompanyName
        : companyName,
    currency_symbol:
      form.includeEngagement === 'yes'
        ? engagement.currencySymbol || engagement.currencyCd || ''
        : '',
    csp_name: form.includeEngagement === 'yes' ? engagement.cspName || '' : '',
    csp_address_line1: form.includeEngagement === 'yes' ? engagement.cspAddressLine1 || '' : '',
    csp_address_line2: form.includeEngagement === 'yes' ? engagement.cspAddressLine2 || '' : '',
    csp_phone: form.includeEngagement === 'yes' ? engagement.cspPhone || '' : '',
    csp_email: form.includeEngagement === 'yes' ? engagement.cspEmail || '' : '',
    csp_website: form.includeEngagement === 'yes' ? engagement.cspWebsite || '' : '',
    fee_incorporation_package:
      form.includeEngagement === 'yes' ? engagement.feeIncorporationPackage : '',
    fee_nominee_director: form.includeEngagement === 'yes' ? engagement.feeNomineeDirector : '',
    fee_nominee_director_deposit:
      form.includeEngagement === 'yes' ? engagement.feeNomineeDirectorDeposit : '',
    fee_employment_pass: form.includeEngagement === 'yes' ? engagement.feeEmploymentPass : '',
    fee_corppass_opening: form.includeEngagement === 'yes' ? engagement.feeCorppassOpening : '',
    fee_bank_account_opening:
      form.includeEngagement === 'yes' ? engagement.feeBankAccountOpening : '',
    fee_annual_secretarial: form.includeEngagement === 'yes' ? engagement.feeAnnualSecretarial : '',
    fee_registered_address: form.includeEngagement === 'yes' ? engagement.feeRegisteredAddress : '',
    fee_annual_registered_address:
      form.includeEngagement === 'yes' ? engagement.feeRegisteredAddress : '',
    fee_annual_corppass_maintenance:
      form.includeEngagement === 'yes' ? engagement.feeAnnualCorppassMaintenance : '',
    fee_annual_nominee_director:
      form.includeEngagement === 'yes' ? engagement.feeAnnualNomineeDirector : '',
    fee_annual_xbrl: form.includeEngagement === 'yes' ? engagement.feeAnnualXbrl : '',
    acceptance_date:
      form.includeEngagement === 'yes' && engagement.acceptanceDate
        ? formatDateByJurisdiction(engagement.acceptanceDate)
        : '',
    financial_year_end_date:
      form.includeEngagement === 'yes' && engagement.financialYearEndDate
        ? formatDateByJurisdiction(engagement.financialYearEndDate)
        : '',

    // ACRA Risk Assessment
    ...Object.fromEntries(Object.entries(risk.srvA).map(([k, v]) => [k, v])),
    ...srvIfFlags,
    ...Object.fromEntries(Object.entries(risk.srvADetails)),
    ...Object.fromEntries(Object.entries(risk.b1Q)),
    ...b1IfFlags,
    ...Object.fromEntries(Object.entries(risk.b2Q)),
    ...b2IfFlags,
    ...Object.fromEntries(Object.entries(risk.b3Q)),
    customer_risk_rating: risk.riskRating,
    risk_rating: risk.riskRating,
    risk_rating_reasons: risk.riskRatingReasons,
    recommendation: risk.recommendation,
    if_recommendation_recommended: risk.recommendation === 'recommended',
    if_recommendation_not_recommended: risk.recommendation === 'not_recommended',
    recommending_officer_name: risk.recommendingOfficerName,
    recommending_officer_position: risk.recommendingOfficerPosition,
    recommending_officer_date: risk.recommendingOfficerDate
      ? formatDateByJurisdiction(risk.recommendingOfficerDate)
      : '',
    // Word signature — 별도 입력이 없으면 성명으로 서명 (이름 필드와 동일하게 채움)
    recommending_officer_signature:
      risk.recommendingOfficerSignature || risk.recommendingOfficerName || '',
    approval: risk.approval,
    if_approval_approved: risk.approval === 'approved',
    if_approval_not_approved: risk.approval === 'not_approved',
    approving_officer_name: risk.approvingOfficerName,
    approving_officer_position: risk.approvingOfficerPosition,
    approving_officer_date: risk.approvingOfficerDate
      ? formatDateByJurisdiction(risk.approvingOfficerDate)
      : '',
    approving_officer_signature:
      risk.approvingOfficerSignature || risk.approvingOfficerName || '',

    // Declaration
    declarant_name: declaration.declarantName,
    declarant_identification_no: declaration.declarantIdentificationNo,
    declaration_date: declaration.declarationDate
      ? formatDateByJurisdiction(declaration.declarationDate)
      : '',
  };
}

/** SG wizard form → INCORPORATION_DATA JSON (stored alongside legacy MY fields) */
export function buildSgIncorporationData(form: SgIncorporationForm, jurisdiction = 'SG') {
  const { applicant, company, engagement } = form;
  const formForJson = stripSgFormFileBlobs(form);
  const docVars = buildSgDocumentVariables(formForJson);

  return {
    jurisdiction,
    formVersion: 'sg_v1',
    sgIncorporationForm: formForJson,
    documentVariables: docVars,
    formFiler: {
      name: `${applicant.filerLastName || ''} ${applicant.filerFirstName || ''}`.trim(),
      lastName: applicant.filerLastName,
      firstName: applicant.filerFirstName,
      email: applicant.filerEmail,
      mobileCountryCode: applicant.filerMobileCountryCode,
      mobileNumber: applicant.filerMobileNumber,
      officeCountryCode: applicant.filerOfficeCountryCode,
      officeNumber: applicant.filerOfficeNumber,
    },
    companyName: company.entityName || engagement.proposedCompanyName || '',
    company_name: company.entityName || engagement.proposedCompanyName || '',
    company_registration_no: company.entityUen,
    entity_uen: company.entityUen,
    countryCd: jurisdiction,
    purpose: 'NEW_INCORPORATION',
    steps: {
      stepFiler: true,
      stepProfile: true,
      stepDirector: true,
      stepSecretary: true,
      stepShareholder: true,
      stepController: true,
      stepEngagement: true,
      stepRisk: true,
      stepReview: true,
    },
    directors: formForJson.directors.map((d) => ({
      directorCd: d.directorCd || newFileCd(),
      firstName: d.directorName.split(' ').slice(-1)[0] || d.directorName,
      lastName: d.directorName.split(' ').slice(0, -1).join(' ') || '',
      director_name: d.directorName,
      email: d.directorEmail,
      contactNumber: formatDirectorMobileNo(d) || formatDirectorTelNo(d),
      nationality: d.directorNationality,
      identificationNo: d.directorIdentificationNo,
      dateOfBirth: d.directorDateOfBirth,
      address: formatSgAddressLine(d.directorAddress),
      appointmentEffectiveDate: d.appointmentEffectiveDate,
      passport: d.passportFileCd ? { fileCd: d.passportFileCd } : undefined,
      proofOfAddress: d.proofOfAddressFileCd ? { fileCd: d.proofOfAddressFileCd } : undefined,
    })),
    shareholders: formForJson.shareholders.map((s) => ({
      shareholderCd: s.shareholderCd || newFileCd(),
      shareholderType: s.shareholderType,
      firstName: s.name,
      name: s.name,
      nationality: s.nationality,
      identificationNo: s.identificationNo,
      numberOfShares: s.noOfShares,
      sharePercentage: s.sharePercentage,
      corporateProfile: s.shareholderType === 'corporate' ? s : undefined,
      passport: s.shareholderType === 'individual' && s.passportFileCd
        ? { fileCd: s.passportFileCd }
        : undefined,
      proofOfAddress: s.shareholderType === 'individual' && s.proofOfAddressFileCd
        ? { fileCd: s.proofOfAddressFileCd }
        : undefined,
    })),
    secretary: formForJson.secretary,
    corporateControllers: formForJson.corporateControllers,
    individualControllers: formForJson.individualControllers,
    engagement: formForJson.engagement,
    riskAssessment: formForJson.risk,
    declaration: formForJson.declaration,
  };
}

function hydrateIdentityFileCds<T extends Record<string, unknown>>(row: T): T {
  const next = { ...row } as any;
  const passportCd =
    next.passportFileCd ||
    next.passport?.fileCd ||
    null;
  const proofCd =
    next.proofOfAddressFileCd ||
    next.proofOfAddress?.fileCd ||
    null;
  next.passportFileCd = passportCd;
  next.proofOfAddressFileCd = proofCd;
  next.passportFile = null;
  next.proofOfAddressFile = null;
  return next;
}

function normalizeLegacyForm(raw: Record<string, unknown>): SgIncorporationForm {
  const form = raw as unknown as SgIncorporationForm & {
    csDirectors?: Record<number, unknown[]>;
    csShareholders?: Record<number, unknown[]>;
  };

  const empty = createSgIncorporationForm();

  if (form.company) {
    const legacy = form.company as SgIncorporationForm['company'] & Record<string, unknown>;
    const legacyUbos = ((legacy.ubos as import('../components/sg/types/sgIncorporation.types').SgUboRow[] | undefined) ?? []).map((u) => ({
      ...createEmptyUbo(),
      ...u,
      residentialAddress: normalizeSgAddressFromLegacy(u.residentialAddress as never),
      pepDetail: u.pepDetail ?? createEmptyPep(),
    }));
    const legacyPeps = (legacy.peps as SgPepRow[] | undefined) ?? [];

    form.company = {
      ...empty.company,
      ...form.company,
      entityRegisteredAddress: normalizeSgAddress(legacy.entityRegisteredAddress as never),
      entityPrincipalBusinessAddress: normalizeSgAddress(legacy.entityPrincipalBusinessAddress as never),
      entityMailingAddress: normalizeSgAddress(legacy.entityMailingAddress as never),
      entityMobileNo: String(legacy.entityMobileNo || legacy.entityMobileNumber || ''),
      entityPrimaryActivityName:
        String(legacy.entityPrimaryActivityName || legacy.entityBusinessActivity || ''),
      sourceOfWealthInfo: legacy.sourceOfWealthInfo ?? '',
      businessWebsite: legacy.businessWebsite ?? '',
      supportingDocumentsNotes: legacy.supportingDocumentsNotes ?? '',
      managingDirectors: form.company.managingDirectors ?? [],
      ceos: form.company.ceos ?? [],
      partners: form.company.partners ?? [],
      others: form.company.others ?? [],
      agentResidentialAddress: normalizeSgAddressFromLegacy(legacy.agentResidentialAddress),
      agentMobileNo: legacy.agentMobileNo || (legacy as { agentContactNo?: string }).agentContactNo || '',
    };

    // Migrate legacy company-level ownership / PEP data into role rows
    const hasLegacyOwnership =
      !!legacy.hasCorporateShareholder
      || legacyUbos.length > 0
      || !!String(legacy.ihcName || '').trim();
    const hasLegacyPep = legacy.hasPep || legacyPeps.length > 0;

    if (hasLegacyOwnership || hasLegacyPep) {
      if (!Array.isArray(form.shareholders)) {
        form.shareholders = [createEmptyShareholder()];
      }
      let corpIdx = form.shareholders.findIndex((s) => s.shareholderType === 'corporate');
      if (hasLegacyOwnership && corpIdx < 0) {
        form.shareholders.push({ ...createEmptyShareholder(), shareholderType: 'corporate' });
        corpIdx = form.shareholders.length - 1;
      }
      if (corpIdx >= 0 && hasLegacyOwnership) {
        const corp = { ...createEmptyShareholder(), ...form.shareholders[corpIdx] };
        corp.shareholderType = 'corporate';
        corp.ihcName = corp.ihcName || String(legacy.ihcName || '');
        corp.ihcIndustry = corp.ihcIndustry || String(legacy.ihcIndustry || '');
        corp.ihcCountry = corp.ihcCountry || String(legacy.ihcCountry || '');
        corp.ihcIsPublic = corp.ihcIsPublic || !!legacy.ihcIsPublic;
        corp.ihcRegistrationNo = corp.ihcRegistrationNo || String(legacy.ihcRegistrationNo || '');
        corp.ihcStockExchange = corp.ihcStockExchange || String(legacy.ihcStockExchange || '');
        corp.ihcOwnershipStructure = corp.ihcOwnershipStructure || String(legacy.ihcOwnershipStructure || '');
        corp.upcName = corp.upcName || String(legacy.upcName || '');
        corp.upcIndustry = corp.upcIndustry || String(legacy.upcIndustry || '');
        corp.upcCountry = corp.upcCountry || String(legacy.upcCountry || '');
        corp.upcIsPublic = corp.upcIsPublic || !!legacy.upcIsPublic;
        corp.upcRegistrationNo = corp.upcRegistrationNo || String(legacy.upcRegistrationNo || '');
        corp.upcStockExchange = corp.upcStockExchange || String(legacy.upcStockExchange || '');
        corp.upcOwnershipStructure = corp.upcOwnershipStructure || String(legacy.upcOwnershipStructure || '');
        if (!corp.ubos?.length && legacyUbos.length) {
          corp.ubos = legacyUbos;
        }
        form.shareholders[corpIdx] = corp;
      }

      if (hasLegacyPep && legacyPeps.length && Array.isArray(form.directors) && !form.directors.some((d) => d.directorIsPep)) {
        const firstPep = legacyPeps[0];
        if (form.directors[0]) {
          form.directors[0] = {
            ...form.directors[0],
            directorIsPep: true,
            pepDetail: { ...createEmptyPep(), ...firstPep },
          };
        }
      }
    }
  }

  if (form.engagement) {
    const eng = { ...empty.engagement, ...form.engagement } as SgIncorporationForm['engagement'] & {
      currencyCd?: string;
      currencySymbol?: string;
    };
    // 레거시: currencySymbol에 S$/USD만 있고 currencyCd 없는 경우
    if (!eng.currencyCd && eng.currencySymbol && !/^B08/i.test(eng.currencySymbol)) {
      eng.currencyCd = '';
      // symbol 유지 — SgCurrencyField가 로드 시 레거시 매핑
    } else if (!eng.currencyCd && eng.currencySymbol && /^B08/i.test(eng.currencySymbol)) {
      eng.currencyCd = eng.currencySymbol;
    }
    form.engagement = eng;
  }

  if (form.secretary) {
    form.secretary = hydrateIdentityFileCds({
      ...empty.secretary,
      ...form.secretary,
      secretaryAddress: normalizeSgAddressFromLegacy(form.secretary.secretaryAddress as never),
      executionDate: form.secretary.executionDate ?? '',
    });
  }

  if (form.risk) {
    const srvA = { ...empty.risk.srvA, ...(form.risk.srvA ?? {}) };
    const srvADetails = { ...empty.risk.srvADetails, ...(form.risk.srvADetails ?? {}) };
    const b1Q = { ...empty.risk.b1Q, ...(form.risk.b1Q ?? {}) };
    const b2Q = { ...empty.risk.b2Q, ...(form.risk.b2Q ?? {}) };
    const b3Q = { ...empty.risk.b3Q, ...(form.risk.b3Q ?? {}) };
    form.risk = { ...empty.risk, ...form.risk, srvA, srvADetails, b1Q, b2Q, b3Q };
  }

  if (Array.isArray(form.corporateControllers)) {
    form.corporateControllers = form.corporateControllers.map((c) => {
      const legacy = c as typeof c & {
        controllerContactCountryCode?: string;
        controllerContactAreaCode?: string;
        controllerContactNo?: string;
      };
      return {
        ...emptyControllerNoticeMeta(),
        ...legacy,
        replyToAddress: normalizeSgAddressFromLegacy(legacy.replyToAddress as never),
        controllerRegisteredOffice: normalizeSgAddressFromLegacy(legacy.controllerRegisteredOffice as never),
        controllerContactCountryCode: legacy.controllerContactCountryCode || '',
        controllerContactAreaCode: legacy.controllerContactAreaCode || '',
        controllerContactNo: legacy.controllerContactNo || '',
        acknowledgementSignerName: legacy.acknowledgementSignerName || '',
        acknowledgementSignerRole: legacy.acknowledgementSignerRole || '',
      };
    });
  }

  if (Array.isArray(form.individualControllers)) {
    form.individualControllers = form.individualControllers.map((c) => {
      const legacy = c as typeof c & {
        controllerContactCountryCode?: string;
        controllerContactAreaCode?: string;
        controllerContactNo?: string;
      };
      return hydrateIdentityFileCds({
        ...emptyControllerNoticeMeta(),
        ...legacy,
        replyToAddress: normalizeSgAddressFromLegacy(legacy.replyToAddress as never),
        controllerResidentialAddress: normalizeSgAddressFromLegacy(legacy.controllerResidentialAddress as never),
        controllerContactCountryCode: legacy.controllerContactCountryCode || '',
        controllerContactAreaCode: legacy.controllerContactAreaCode || '',
        controllerContactNo: legacy.controllerContactNo || '',
      });
    });
  }

  if (Array.isArray(form.directors)) {
    form.directors = form.directors.map((d) => {
      const legacy = d as SgIncorporationForm['directors'][0] & {
        directorContactNo?: string;
        directorTelNo?: string;
        directorMobileNo?: string;
      };
      const base = hydrateIdentityFileCds({ ...createEmptyDirector(), ...legacy });
      base.directorAddress = normalizeSgAddressFromLegacy(legacy.directorAddress as never);
      base.directorAlternateAddress = normalizeSgAddressFromLegacy(legacy.directorAlternateAddress as never);
      base.directorIsPep = legacy.directorIsPep ?? false;
      base.pepDetail = { ...createEmptyPep(), ...(legacy.pepDetail ?? {}) };

      if (typeof legacy.directorMobileNo === 'string' && legacy.directorMobileNo && !base.directorMobileNo) {
        base.directorMobileNo = legacy.directorMobileNo;
      }
      if (legacy.directorContactNo && !base.directorMobileNo) {
        base.directorMobileNo = legacy.directorContactNo;
      }
      if (typeof legacy.directorTelNo === 'string' && legacy.directorTelNo && !base.directorTelNo) {
        base.directorTelNo = legacy.directorTelNo;
      }

      return base;
    });
  }

  if (Array.isArray(form.shareholders)) {
    const legacyCsDirectors = form.csDirectors;
    const legacyCsShareholders = form.csShareholders;
    form.shareholders = form.shareholders.map((s, i) => {
      const legacy = s as SgIncorporationForm['shareholders'][0] & {
        csBusinessActivity?: string;
        csMobileNo?: string;
        csContactNoOffice?: string;
      };
      const base = hydrateIdentityFileCds({ ...createEmptyShareholder(), ...s });
      base.csRegisteredAddress = normalizeSgAddressFromLegacy(legacy.csRegisteredAddress as never);
      base.csIsPublicListed = legacy.csIsPublicListed ?? false;
      base.csBusinessActivityName =
        legacy.csBusinessActivityName || legacy.csBusinessActivity || '';
      base.address = normalizeSgAddressFromLegacy(legacy.address as never);
      base.isPep = legacy.isPep ?? false;
      base.pepDetail = { ...createEmptyPep(), ...(legacy.pepDetail ?? {}) };
      base.ubos = (base.ubos ?? []).map((u) => ({
        ...createEmptyUbo(),
        ...u,
        residentialAddress: normalizeSgAddressFromLegacy(u.residentialAddress as never),
        pepDetail: { ...createEmptyPep(), ...(u.pepDetail ?? {}) },
      }));
      base.allotments = (base.allotments ?? []).map((a) => ({
        currency: a?.currency ?? '',
        shareType: a?.shareType ?? 'ORD',
        noOfShares: a?.noOfShares ?? '',
        paidUpCapital: a?.paidUpCapital ?? '',
        heldInTrust: a?.heldInTrust ?? false,
        provideNameOfTrust: a?.provideNameOfTrust ?? false,
        nameOfTrust: a?.nameOfTrust ?? '',
        subClasses: a?.subClasses ?? [],
      }));
      if (typeof legacy.csMobileNo === 'string' && legacy.csMobileNo && !base.csMobileNo) {
        base.csMobileNo = legacy.csMobileNo;
      }
      if (typeof legacy.csContactNoOffice === 'string' && legacy.csContactNoOffice && !base.csContactNoOffice) {
        base.csContactNoOffice = legacy.csContactNoOffice;
      }
      base.csDirectors = (base.csDirectors ?? []).map((d) => ({
        ...createEmptyCsDirector(),
        ...d,
        residentialAddress: normalizeSgAddressFromLegacy(d.residentialAddress as never),
      }));
      base.csShareholders = (base.csShareholders ?? []).map((sh) => ({
        ...createEmptyCsShareholder(),
        ...sh,
        residentialAddress: normalizeSgAddressFromLegacy(sh.residentialAddress as never),
      }));
      if (!base.csDirectors.length) {
        base.csDirectors = (legacyCsDirectors?.[i] as typeof base.csDirectors) ?? [createEmptyCsDirector()];
      }
      if (!base.csShareholders.length) {
        base.csShareholders = (legacyCsShareholders?.[i] as typeof base.csShareholders) ?? [createEmptyCsShareholder()];
      }
      return base;
    });
    delete form.csDirectors;
    delete form.csShareholders;
  }

  // Optional section Yes/No — 레거시에 플래그 없으면 기존 입력 유무로 추론
  const asFlag = (v: unknown): '' | 'yes' | 'no' =>
    v === 'yes' || v === 'no' ? v : '';
  form.includeDirectors = asFlag(form.includeDirectors) || 'no';
  form.includeSecretary = asFlag(form.includeSecretary) || 'no';
  form.includeShareholders = asFlag(form.includeShareholders) || 'no';
  form.includeControllers = asFlag(form.includeControllers) || 'no';
  form.includeEngagement = asFlag(form.includeEngagement) || 'no';
  if (
    form.includeDirectors === 'no' &&
    !('includeDirectors' in (raw || {})) &&
    Array.isArray(form.directors) &&
    form.directors.some((d) => String(d.directorName || '').trim())
  ) {
    form.includeDirectors = 'yes';
  }
  if (
    form.includeSecretary === 'no' &&
    !('includeSecretary' in (raw || {})) &&
    String(form.secretary?.secretaryName || '').trim()
  ) {
    form.includeSecretary = 'yes';
  }
  if (
    form.includeShareholders === 'no' &&
    !('includeShareholders' in (raw || {})) &&
    Array.isArray(form.shareholders) &&
    form.shareholders.some((s) => String(s.name || '').trim())
  ) {
    form.includeShareholders = 'yes';
  }
  if (
    form.includeControllers === 'no' &&
    !('includeControllers' in (raw || {})) &&
    ((form.corporateControllers?.length ?? 0) > 0 ||
      (form.individualControllers?.length ?? 0) > 0)
  ) {
    form.includeControllers = 'yes';
  }
  if (
    form.includeEngagement === 'no' &&
    !('includeEngagement' in (raw || {})) &&
    (String(form.engagement?.cspName || '').trim() ||
      String(form.engagement?.feeIncorporationPackage || '').trim() ||
      String(form.engagement?.proposedCompanyName || '').trim())
  ) {
    form.includeEngagement = 'yes';
  }

  return form;
}

export function extractSgFormFromIncorporationData(data: Record<string, unknown> | null | undefined): SgIncorporationForm | null {
  if (!data) return null;
  const sg = data.sgIncorporationForm as Record<string, unknown> | undefined;
  if (sg && typeof sg === 'object') return normalizeLegacyForm(sg);
  return null;
}
